import { useState, useEffect, useMemo, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Typography,
  Container,
  Paper,
  Box,
  IconButton,
  Grid,
  TextField,
  Button,
  Tabs,
  Tab,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  MenuItem as MenuItemMUI,
} from '@mui/material';
import {
  ArrowBack,
  Print,
  Delete,
  Star,
  StarBorder,
  CheckCircle,
  ContentCopy,
  Science,
  OpenInNew,
} from '@mui/icons-material';
import TopBar from '../../components/TopBar';
import {
  patientService,
  prescriptionService,
  medicineService,
  adviceService,
  investigationService,
  referDoctorService,
  authService,
  printSettingsService,
  appointmentService,
  doctorFavouriteService,
} from '../../services/api';

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';

const API_BASE = (process.env.REACT_APP_API_URL || 'http://localhost:5000/api').replace('/api', '');

const PatientDetailPage = () => {
  const navigate = useNavigate();
  const { id: patientId, appointmentId } = useParams();
  const user = authService.getCurrentUser();
  const [patient, setPatient] = useState(null);
  const [patientHistory, setPatientHistory] = useState([]);
  const [lastPrescription, setLastPrescription] = useState(null);
  const [tabValue, setTabValue] = useState(0);
  const [vitals, setVitals] = useState({
    bp: '',
    pulse: '',
    weight: '',
    spo2: '',
    gestationalAge: '',
    edd: '',
  });
  const [selectedMedicines, setSelectedMedicines] = useState([]);
  const [selectedAdvices, setSelectedAdvices] = useState([]);
  const [availableMedicines, setAvailableMedicines] = useState([]);
  const [availableAdvices, setAvailableAdvices] = useState([]);
  const [availableInvestigations, setAvailableInvestigations] = useState([]);
  const [selectedInvestigations, setSelectedInvestigations] = useState([]);
  const [printSettings, setPrintSettings] = useState(null);
  const [prescriptionId, setPrescriptionId] = useState(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [vitalsHistoryOpen, setVitalsHistoryOpen] = useState(false);
  const [vitalsHistoryData, setVitalsHistoryData] = useState([]);
  const [chiefComplaint, setChiefComplaint] = useState('');
  const [provisionalDiagnosis, setProvisionalDiagnosis] = useState('');
  const [allergy, setAllergy] = useState('');
  const [importantNotes, setImportantNotes] = useState('');
  const [followUpDate, setFollowUpDate] = useState('');
  const [referDoctor, setReferDoctor] = useState('');
  const [availableReferDoctors, setAvailableReferDoctors] = useState([]);
  const [medicineSearch, setMedicineSearch] = useState('');
  const [adviceSearch, setAdviceSearch] = useState('');
  const [investigationSearch, setInvestigationSearch] = useState('');
  const [favMedicineIds,  setFavMedicineIds]  = useState(new Set());
  const [favAdviceIds,    setFavAdviceIds]    = useState(new Set());
  const [doctorMedHotkeys, setDoctorMedHotkeys] = useState({});  // { [medicineId]: string }
  const [doctorAdvHotkeys, setDoctorAdvHotkeys] = useState({});  // { [adviceId]: string }
  const [printConfirmOpen, setPrintConfirmOpen] = useState(false);
  const [pendingAppointmentPayload, setPendingAppointmentPayload] = useState(null);
  const [labReports, setLabReports] = useState([]);
  const [reportViewerOpen, setReportViewerOpen] = useState(false);
  const [viewingReport, setViewingReport] = useState(null);

  // Load initial data only once when component mounts
  useEffect(() => {
    loadPatientData();
    loadMedicinesAndAdvices();
    loadPrintSettings();
  }, [patientId, appointmentId]); // Only re-run if patient/appointment changes
  
  // Setup hotkey listener separately
  useEffect(() => {
    const handleKeyPress = async (e) => {
      if (e.ctrlKey || e.metaKey) {
        const key = e.key.toLowerCase();
        
        // Favourites use doctor personal hotkey (falls back to global); check favourites first
        let medicine = availableMedicines.find(m =>
          favMedicineIds.has(m.id) &&
          (doctorMedHotkeys[m.id] || m.hotkey)?.toLowerCase() === key
        );
        if (!medicine) {
          medicine = availableMedicines.find(m =>
            !favMedicineIds.has(m.id) && m.hotkey?.toLowerCase() === key
          );
        }
        if (medicine) { e.preventDefault(); addMedicine(medicine); }

        let advice = availableAdvices.find(a =>
          favAdviceIds.has(a.id) &&
          (doctorAdvHotkeys[a.id] || a.hotkey)?.toLowerCase() === key
        );
        if (!advice) {
          advice = availableAdvices.find(a =>
            !favAdviceIds.has(a.id) && a.hotkey?.toLowerCase() === key
          );
        }
        if (advice) { e.preventDefault(); addAdvice(advice); }
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [availableMedicines, availableAdvices, favMedicineIds, favAdviceIds, doctorMedHotkeys, doctorAdvHotkeys]);

  const loadPatientData = async () => {
    try {
      // Fire all independent calls in parallel
      const [patientResult, historyResult, lastPrescResult, labResult, appointmentResult] =
        await Promise.allSettled([
          patientService.getPatient(patientId),
          patientService.getPatientHistory(patientId),
          prescriptionService.getLastPrescription(patientId),
          patientService.getLabReports(patientId),
          appointmentId ? appointmentService.getAppointment(appointmentId) : Promise.resolve(null),
        ]);

      // Patient (required)
      if (patientResult.status === 'rejected') {
        console.error('Error loading patient data:', patientResult.reason);
        return;
      }
      const patientData = patientResult.value.data;
      setPatient(patientData);

      const rawEdd = patientData.edd || patientData.EDD;
      const formattedEdd = rawEdd
        ? new Date(rawEdd).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';
      setVitals(prev => ({ ...prev, edd: formattedEdd }));

      // History
      if (historyResult.status === 'fulfilled') setPatientHistory(historyResult.value.data);
      else setPatientHistory([]);

      // Last prescription
      if (lastPrescResult.status === 'fulfilled') setLastPrescription(lastPrescResult.value.data);
      else setLastPrescription(null);

      // Lab reports
      if (labResult.status === 'fulfilled') setLabReports(labResult.value.data);
      else setLabReports([]);

      // Appointment vitals
      if (appointmentId && appointmentResult.status === 'fulfilled' && appointmentResult.value) {
        const appointment = appointmentResult.value.data;
        if (appointment.notes) setChiefComplaint(appointment.notes);
        setVitals({
          bp: appointment.bp || '',
          pulse: appointment.pulse || '',
          weight: appointment.weight || '',
          spo2: appointment.spO2 || appointment.spo2 || '',
          gestationalAge: appointment.gestationalAge || '',
          edd: formattedEdd,
        });
      }
    } catch (error) {
      console.error('Error loading patient data:', error);
    }
  };

  const loadMedicinesAndAdvices = async () => {
    try {
      const [medicinesRes, advicesRes, investigationsRes, referDoctorsRes, favMedRes, favAdvRes] = await Promise.all([
        medicineService.getAllMedicines(),
        adviceService.getAllAdvices(),
        investigationService.getAllInvestigations(),
        referDoctorService.getAllReferDoctors(),
        doctorFavouriteService.getMedicines(),
        doctorFavouriteService.getAdvices(),
      ]);
      setAvailableMedicines(medicinesRes.data);
      setAvailableAdvices(advicesRes.data);
      setAvailableInvestigations(investigationsRes.data);
      setAvailableReferDoctors(referDoctorsRes.data);

      const favMedIds = new Set((favMedRes.data || []).map(f => f.id));
      const medHotkeys = {};
      (favMedRes.data || []).forEach(f => { if (f.doctorHotkey) medHotkeys[f.id] = f.doctorHotkey; });
      setFavMedicineIds(favMedIds);
      setDoctorMedHotkeys(medHotkeys);

      const favAdvIds = new Set((favAdvRes.data || []).map(f => f.id));
      const advHotkeys = {};
      (favAdvRes.data || []).forEach(f => { if (f.doctorHotkey) advHotkeys[f.id] = f.doctorHotkey; });
      setFavAdviceIds(favAdvIds);
      setDoctorAdvHotkeys(advHotkeys);
    } catch (error) {
      console.error('Error loading medicines, advices and investigations:', error);
    }
  };

  const loadPrintSettings = async () => {
    try {
      const response = await printSettingsService.getSettings();
      setPrintSettings(response.data);
      console.log('Print settings loaded');
    } catch (error) {
      console.log('No print settings found (this is OK) - will use defaults');
      setPrintSettings(null);
    }
  };

  const handleOpenVitalsHistory = async () => {
    try {
      const res = await appointmentService.getAppointmentsByPatient(patientId);
      const data = res.data
        .filter(a => a.bp || a.pulse || a.weight || a.spO2 || a.spo2)
        .map(a => ({
          date: new Date(a.appointmentDate).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: '2-digit' }),
          bp: a.bp ? parseFloat(a.bp.split('/')[0]) : null,
          bpDiastolic: a.bp ? parseFloat(a.bp.split('/')[1]) : null,
          pulse: a.pulse ? parseFloat(a.pulse) : null,
          weight: a.weight ? parseFloat(a.weight) : null,
          spo2: a.spO2 ? parseFloat(a.spO2) : (a.spo2 ? parseFloat(a.spo2) : null),
        }));
      setVitalsHistoryData(data);
    } catch (err) {
      setVitalsHistoryData([]);
    }
    setVitalsHistoryOpen(true);
  };


  const handleVitalsChange = (e) => {
    setVitals({
      ...vitals,
      [e.target.name]: e.target.value,
    });
  };

  const addMedicine = useCallback((medicine) => {
    setSelectedMedicines(prev => {
      if (prev.find(m => m.id === medicine.id)) return prev;
      return [...prev, { ...medicine, dosage: medicine.dosage || '', duration: '', mealPref: '' }];
    });
  }, []);

  const removeMedicine = (medicineId) => {
    setSelectedMedicines(selectedMedicines.filter(m => m.id !== medicineId));
  };

  const updateMedicine = (medicineId, field, value) => {
    setSelectedMedicines(
      selectedMedicines.map(m =>
        m.id === medicineId ? { ...m, [field]: value } : m
      )
    );
  };

  const addAdvice = useCallback((advice) => {
    setSelectedAdvices(prev => {
      if (prev.find(a => a.id === advice.id)) return prev;
      return [...prev, advice];
    });
  }, []);

  const removeAdvice = (adviceId) => {
    setSelectedAdvices(selectedAdvices.filter(a => a.id !== adviceId));
  };

  const addInvestigation = useCallback((investigation) => {
    setSelectedInvestigations(prev => {
      if (prev.find(i => i.id === investigation.id)) return prev;
      return [...prev, investigation];
    });
  }, []);

  const removeInvestigation = (investigationId) => {
    setSelectedInvestigations(selectedInvestigations.filter(i => i.id !== investigationId));
  };

  const toggleFavMedicine = useCallback(async (medicine) => {
    const isFav = favMedicineIds.has(medicine.id);
    if (isFav) {
      setFavMedicineIds(prev => { const s = new Set(prev); s.delete(medicine.id); return s; });
    } else {
      setFavMedicineIds(prev => new Set([...prev, medicine.id]));
    }
    try {
      if (isFav) await doctorFavouriteService.removeMedicine(medicine.id);
      else        await doctorFavouriteService.addMedicine(medicine.id);
    } catch {
      // revert
      if (isFav) setFavMedicineIds(prev => new Set([...prev, medicine.id]));
      else       setFavMedicineIds(prev => { const s = new Set(prev); s.delete(medicine.id); return s; });
    }
  }, [favMedicineIds]);

  const toggleFavAdvice = useCallback(async (advice) => {
    const isFav = favAdviceIds.has(advice.id);
    if (isFav) {
      setFavAdviceIds(prev => { const s = new Set(prev); s.delete(advice.id); return s; });
    } else {
      setFavAdviceIds(prev => new Set([...prev, advice.id]));
    }
    try {
      if (isFav) await doctorFavouriteService.removeAdvice(advice.id);
      else        await doctorFavouriteService.addAdvice(advice.id);
    } catch {
      if (isFav) setFavAdviceIds(prev => new Set([...prev, advice.id]));
      else       setFavAdviceIds(prev => { const s = new Set(prev); s.delete(advice.id); return s; });
    }
  }, [favAdviceIds]);

  const handleMedHotkeyChange = useCallback((medicineId, value) =>
    setDoctorMedHotkeys(prev => ({ ...prev, [medicineId]: value })), []);

  const handleMedHotkeyBlur = useCallback(async (medicineId) => {
    try { await doctorFavouriteService.updateMedicineHotkey(medicineId, doctorMedHotkeys[medicineId] || ''); } catch { /* ignore */ }
  }, [doctorMedHotkeys]);

  const handleAdvHotkeyChange = useCallback((adviceId, value) =>
    setDoctorAdvHotkeys(prev => ({ ...prev, [adviceId]: value })), []);

  const handleAdvHotkeyBlur = useCallback(async (adviceId) => {
    try { await doctorFavouriteService.updateAdviceHotkey(adviceId, doctorAdvHotkeys[adviceId] || ''); } catch { /* ignore */ }
  }, [doctorAdvHotkeys]);

  // Auto-formats dosage digits into morning-afternoon-evening groups.
  // 3 digits â†’ 1-1-1 (each char is one group)
  // 6 digits â†’ 10-10-10 (every two chars is one group)
  // Non-divisible-by-3 lengths are shown as raw digits while typing.
  const formatDosage = (value) => {
    const digits = value.replace(/[^0-9]/g, '');
    if (!digits) return '';
    const len = digits.length;
    if (len >= 3 && len % 3 === 0) {
      const g = len / 3;
      return `${digits.slice(0, g)}-${digits.slice(g, g * 2)}-${digits.slice(g * 2)}`;
    }
    return digits;
  };

  const triggerPrint = () => {
    // Must be set BEFORE window.print() — in Chrome, print() blocks synchronously
    // and afterprint fires before it returns, so setting it after is always too late.
    window.onafterprint = () => {
      window.onafterprint = null;
      setPrintConfirmOpen(true);
    };
    window.print();
  };

  const handlePrint = async () => {
    const prescriptionData = {
      patientId: patientId,
      doctorId: user.id,
      vitals,
      medicines: selectedMedicines.map(m => ({
        id: m.id,
        dosage: m.dosage,
        duration: m.duration,
        mealPref: m.mealPref || null,
      })),
      advices: selectedAdvices,
      investigations: selectedInvestigations,
      chiefComplaint: chiefComplaint || null,
      provisionalDiagnosis: provisionalDiagnosis || null,
      allergy: allergy || null,
      medicalNotes: importantNotes || null,
      appointmentId: appointmentId ? parseInt(appointmentId) : null,
      followUpDate: followUpDate ? new Date(followUpDate).toISOString() : null,
      referDoctorName: referDoctor || null,
      date: new Date().toISOString(),
    };

    try {
      const response = await prescriptionService.createPrescription(prescriptionData);

      // Flush prescription ID into DOM synchronously before print dialog opens
      flushSync(() => setPrescriptionId(response.data.id));

      // Pre-fetch appointment data so it's ready when doctor confirms
      if (appointmentId) {
        try {
          const appointmentRes = await appointmentService.getAppointment(appointmentId);
          const current = appointmentRes.data;
          setPendingAppointmentPayload({
            id: parseInt(appointmentId),
            patientId: current.patientId,
            doctorId: current.doctorId,
            appointmentDate: current.appointmentDate,
            appointmentTime: current.appointmentTime,
            type: current.type,
            notes: current.notes,
            status: 'Completed',
            bp: current.bp,
            pulse: current.pulse,
            weight: current.weight,
            spO2: current.spO2 || current.spo2,
            gestationalAge: current.gestationalAge,
          });
        } catch (err) {
          console.error('Error fetching appointment:', err);
        }
      }

      triggerPrint();
    } catch (error) {
      console.error('Error creating prescription:', error);
    }
  };

  const handlePrintConfirmYes = async () => {
    setPrintConfirmOpen(false);
    if (pendingAppointmentPayload) {
      try {
        await appointmentService.updateAppointment(appointmentId, pendingAppointmentPayload);
      } catch (err) {
        console.error('Error updating appointment status:', err);
      }
    }
    navigate('/opd');
  };

  const handlePrintAgain = () => {
    setPrintConfirmOpen(false);
    // Small delay to let the dialog fully close before the print dialog opens
    setTimeout(() => triggerPrint(), 300);
  };

  const handleCopyLastPrescription = () => {
    if (!lastPrescription) return;

    setProvisionalDiagnosis(lastPrescription.provisionalDiagnosis || '');
    setAllergy(lastPrescription.allergy || '');
    setImportantNotes(lastPrescription.medicalNotes || '');

    const copiedMedicines = (lastPrescription.prescriptionMedicines || [])
      .filter(pm => pm.medicine)
      .map(pm => ({
        ...pm.medicine,
        dosage: pm.dosage || '',
        duration: pm.duration || '',
        mealPref: pm.mealPref || '',
      }));
    setSelectedMedicines(copiedMedicines);

    const copiedAdvices = (lastPrescription.prescriptionAdvices || [])
      .filter(pa => pa.advice)
      .map(pa => pa.advice);
    setSelectedAdvices(copiedAdvices);

    const copiedInvestigations = (lastPrescription.prescriptionInvestigations || [])
      .filter(pi => pi.investigation)
      .map(pi => pi.investigation);
    setSelectedInvestigations(copiedInvestigations);
  };

  const calculateAge = (dob) => {
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const canPrint =
    selectedMedicines.length > 0 ||
    selectedAdvices.length > 0 ||
    selectedInvestigations.length > 0 ||
    importantNotes.trim().length > 0;

  // O(1) selected-item lookup — prevents O(n²) .find() inside table rows on every render
  const selectedMedicineIds = useMemo(() => new Set(selectedMedicines.map(m => m.id)), [selectedMedicines]);
  const selectedAdviceIds = useMemo(() => new Set(selectedAdvices.map(a => a.id)), [selectedAdvices]);
  const selectedInvestigationIds = useMemo(() => new Set(selectedInvestigations.map(i => i.id)), [selectedInvestigations]);

  // Memoized filtered lists — only recomputed when search text or source list changes,
  // NOT on every provisionalDiagnosis / chiefComplaint keystroke
  const medSearchLower = medicineSearch.toLowerCase();
  const filteredFavMedicines = useMemo(
    () => availableMedicines.filter(m => favMedicineIds.has(m.id) && m.name.toLowerCase().includes(medSearchLower)),
    [availableMedicines, favMedicineIds, medSearchLower]
  );
  const filteredNonFavMedicines = useMemo(
    () => availableMedicines.filter(m => !favMedicineIds.has(m.id) && m.name.toLowerCase().includes(medSearchLower)),
    [availableMedicines, favMedicineIds, medSearchLower]
  );

  const advSearchLower = adviceSearch.toLowerCase();
  const filteredFavAdvices = useMemo(
    () => availableAdvices.filter(a => favAdviceIds.has(a.id) && a.text.toLowerCase().includes(advSearchLower)),
    [availableAdvices, favAdviceIds, advSearchLower]
  );
  const filteredNonFavAdvices = useMemo(
    () => availableAdvices.filter(a => !favAdviceIds.has(a.id) && a.text.toLowerCase().includes(advSearchLower)),
    [availableAdvices, favAdviceIds, advSearchLower]
  );

  const invSearchLower = investigationSearch.toLowerCase();
  const filteredInvestigations = useMemo(
    () => availableInvestigations.filter(i => i.name.toLowerCase().includes(invSearchLower)),
    [availableInvestigations, invSearchLower]
  );

  // Memoize the actual JSX rows for each tab — this prevents the expensive .map() calls
  // from running on every unrelated state change (e.g. typing in Provisional Diagnosis).
  const medicinesTabRows = useMemo(() => (
    <>
      {filteredFavMedicines.map(medicine => {
        const isSelected = selectedMedicineIds.has(medicine.id);
        const hk = doctorMedHotkeys[medicine.id] !== undefined ? doctorMedHotkeys[medicine.id] : '';
        return (
          <TableRow
            key={medicine.id}
            onClick={() => addMedicine(medicine)}
            sx={{ cursor: 'pointer', backgroundColor: isSelected ? '#bbdefb' : '#fff8e1', '&:hover': { backgroundColor: isSelected ? '#90caf9' : '#ffe082' } }}
          >
            <TableCell sx={{ fontSize: '0.8rem', py: 0.5, fontWeight: isSelected ? 700 : 600 }}>{medicine.name}</TableCell>
            <TableCell sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
              <TextField
                size="small"
                value={hk}
                onChange={e => handleMedHotkeyChange(medicine.id, e.target.value.slice(-1))}
                onBlur={() => handleMedHotkeyBlur(medicine.id)}
                placeholder="key"
                inputProps={{ maxLength: 1, style: { fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px', width: 28 } }}
                title="Ctrl + this key"
              />
            </TableCell>
            <TableCell sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
              <IconButton size="small" color="warning" onClick={() => toggleFavMedicine(medicine)}>
                <Star sx={{ fontSize: 16 }} />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
      {filteredFavMedicines.length > 0 && (
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0.4, bgcolor: '#f5f5f5', fontSize: '0.68rem', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
            ALL MEDICINES
          </TableCell>
        </TableRow>
      )}
      {filteredNonFavMedicines.map(medicine => {
        const isSelected = selectedMedicineIds.has(medicine.id);
        return (
          <TableRow
            key={medicine.id}
            onClick={() => addMedicine(medicine)}
            sx={{ cursor: 'pointer', backgroundColor: isSelected ? '#e3f2fd' : 'transparent', '&:hover': { backgroundColor: isSelected ? '#bbdefb' : '#f5f5f5' } }}
          >
            <TableCell sx={{ fontSize: '0.8rem', py: 0.75, fontWeight: isSelected ? 600 : 400 }}>{medicine.name}</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', py: 0.75, color: 'text.secondary' }}>
              {medicine.hotkey ? `Ctrl+${medicine.hotkey}` : '—'}
            </TableCell>
            <TableCell sx={{ py: 0.75 }} onClick={e => e.stopPropagation()}>
              <IconButton size="small" onClick={() => toggleFavMedicine(medicine)} title="Add to My List">
                <StarBorder sx={{ fontSize: 16, color: '#bdbdbd' }} />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  ), [filteredFavMedicines, filteredNonFavMedicines, selectedMedicineIds, doctorMedHotkeys,
      addMedicine, toggleFavMedicine, handleMedHotkeyChange, handleMedHotkeyBlur]);

  const adviceTabRows = useMemo(() => (
    <>
      {filteredFavAdvices.map(advice => {
        const isSelected = selectedAdviceIds.has(advice.id);
        const hk = doctorAdvHotkeys[advice.id] !== undefined ? doctorAdvHotkeys[advice.id] : '';
        return (
          <TableRow
            key={advice.id}
            onClick={() => addAdvice(advice)}
            sx={{ cursor: 'pointer', backgroundColor: isSelected ? '#e8f5e9' : '#fff8e1', '&:hover': { backgroundColor: isSelected ? '#c8e6c9' : '#ffe082' } }}
          >
            <TableCell sx={{ fontSize: '0.8rem', py: 0.5, fontWeight: isSelected ? 700 : 600 }}>{advice.text}</TableCell>
            <TableCell sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
              <TextField
                size="small"
                value={hk}
                onChange={e => handleAdvHotkeyChange(advice.id, e.target.value.slice(-1))}
                onBlur={() => handleAdvHotkeyBlur(advice.id)}
                placeholder="key"
                inputProps={{ maxLength: 1, style: { fontSize: '0.72rem', textAlign: 'center', padding: '2px 4px', width: 28 } }}
                title="Ctrl + this key"
              />
            </TableCell>
            <TableCell sx={{ py: 0.5 }} onClick={e => e.stopPropagation()}>
              <IconButton size="small" color="warning" onClick={() => toggleFavAdvice(advice)}>
                <Star sx={{ fontSize: 16 }} />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
      {filteredFavAdvices.length > 0 && (
        <TableRow>
          <TableCell colSpan={3} sx={{ py: 0.4, bgcolor: '#f5f5f5', fontSize: '0.68rem', color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>
            ALL ADVICE
          </TableCell>
        </TableRow>
      )}
      {filteredNonFavAdvices.map(advice => {
        const isSelected = selectedAdviceIds.has(advice.id);
        return (
          <TableRow
            key={advice.id}
            onClick={() => addAdvice(advice)}
            sx={{ cursor: 'pointer', backgroundColor: isSelected ? '#f3e5f5' : 'transparent', '&:hover': { backgroundColor: isSelected ? '#e1bee7' : '#f5f5f5' } }}
          >
            <TableCell sx={{ fontSize: '0.8rem', py: 0.75, fontWeight: isSelected ? 600 : 400 }}>{advice.text}</TableCell>
            <TableCell sx={{ fontSize: '0.75rem', py: 0.75, color: 'text.secondary' }}>
              {advice.hotkey ? `Ctrl+${advice.hotkey}` : '—'}
            </TableCell>
            <TableCell sx={{ py: 0.75 }} onClick={e => e.stopPropagation()}>
              <IconButton size="small" onClick={() => toggleFavAdvice(advice)} title="Add to My List">
                <StarBorder sx={{ fontSize: 16, color: '#bdbdbd' }} />
              </IconButton>
            </TableCell>
          </TableRow>
        );
      })}
    </>
  ), [filteredFavAdvices, filteredNonFavAdvices, selectedAdviceIds, doctorAdvHotkeys,
      addAdvice, toggleFavAdvice, handleAdvHotkeyChange, handleAdvHotkeyBlur]);

  const investigationsTabRows = useMemo(() => (
    <>
      {filteredInvestigations.map((investigation) => {
        const isSelected = selectedInvestigationIds.has(investigation.id);
        return (
          <TableRow
            key={investigation.id}
            onClick={() => addInvestigation(investigation)}
            sx={{
              cursor: 'pointer',
              backgroundColor: isSelected ? '#e8f5e9' : 'transparent',
              '&:hover': { backgroundColor: isSelected ? '#c8e6c9' : '#f5f5f5' },
            }}
          >
            <TableCell sx={{ fontSize: '0.8rem', py: 0.75, fontWeight: isSelected ? 600 : 400 }}>
              {investigation.name}
            </TableCell>
            <TableCell sx={{ fontSize: '0.75rem', py: 0.75, color: 'text.secondary' }}>
              {investigation.hotkey ? `Ctrl+${investigation.hotkey}` : '—'}
            </TableCell>
          </TableRow>
        );
      })}
    </>
  ), [filteredInvestigations, selectedInvestigationIds, addInvestigation]);

  const isPrePrinted = (printSettings?.letterheadType || printSettings?.LetterheadType || 'Pre-Printed Letterhead') === 'Pre-Printed Letterhead';
  const mt = printSettings?.marginTop    ?? printSettings?.MarginTop    ?? 10;
  const mb = printSettings?.marginBottom ?? printSettings?.MarginBottom ?? 10;
  const ml = printSettings?.marginLeft   ?? printSettings?.MarginLeft   ?? 10;
  const mr = printSettings?.marginRight  ?? printSettings?.MarginRight  ?? 10;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      <TopBar title="Patient Details" onBack={() => navigate(-1)} noPrint />

      <Container maxWidth="xl" sx={{ py: 2 }}>
        <Grid container spacing={2}>
          {/* Left Panel - Patient Info & History */}
          <Grid item xs={12} md={3} className="no-print">
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Patient Information
              </Typography>
              <Divider sx={{ mb: 2 }} />
              {patient && (
                <Box>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Name:</strong> {patient.firstName} {patient.lastName}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Age:</strong> {calculateAge(patient.dateOfBirth)} years
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Gender:</strong> {patient.gender}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Blood Group:</strong> {patient.bloodGroup || 'N/A'}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Phone:</strong> {patient.phoneNumber}
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 1 }}>
                    <strong>Emergency Contact:</strong> {patient.emergencyContact}
                  </Typography>
                </Box>
              )}
            </Paper>

            {/* Vitals Input */}
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Vitals
                </Typography>
                <Button
                  size="small"
                  variant="text"
                  onClick={handleOpenVitalsHistory}
                  sx={{ textTransform: 'none', fontWeight: 500 }}
                >
                  Vitals History
                </Button>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="BP"
                    name="bp"
                    value={vitals.bp}
                    onChange={handleVitalsChange}
                    placeholder="120/80"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Pulse"
                    name="pulse"
                    value={vitals.pulse}
                    onChange={handleVitalsChange}
                    placeholder="72"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Weight (kg)"
                    name="weight"
                    value={vitals.weight}
                    onChange={handleVitalsChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="SPO2"
                    name="spo2"
                    value={vitals.spo2}
                    onChange={handleVitalsChange}
                    placeholder="98%"
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Gestational Age"
                    name="gestationalAge"
                    value={vitals.gestationalAge}
                    onChange={handleVitalsChange}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="EDD"
                    name="edd"
                    value={vitals.edd}
                    onChange={handleVitalsChange}
                    placeholder="dd/mm/yyyy"
                  />
                </Grid>
              </Grid>
            </Paper>

            {/* Last Prescription */}
            {lastPrescription && (
              <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Last Prescription
                  </Typography>
                  <Button
                    size="small"
                    variant="text"
                    onClick={() => setHistoryOpen(true)}
                    sx={{ textTransform: 'none', fontWeight: 500 }}
                  >
                    Prescription History
                  </Button>
                </Box>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Date: {new Date(lastPrescription.date).toLocaleDateString()}
                </Typography>
                {lastPrescription.prescriptionMedicines?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Medicines:</Typography>
                    {lastPrescription.prescriptionMedicines.map((m) => (
                      <Typography key={m.id} variant="body2" color="text.secondary">
                        • {m.medicine?.name} — {m.dosage} {m.duration ? `(${m.duration})` : ''}
                      </Typography>
                    ))}
                  </Box>
                )}
                {lastPrescription.prescriptionAdvices?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Advice:</Typography>
                    {lastPrescription.prescriptionAdvices.map((a) => (
                      <Typography key={a.id} variant="body2" color="text.secondary">
                        • {a.advice?.text}
                      </Typography>
                    ))}
                  </Box>
                )}
                {lastPrescription.prescriptionInvestigations?.length > 0 && (
                  <Box sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>Investigations:</Typography>
                    {lastPrescription.prescriptionInvestigations.map((pi) => (
                      <Typography key={pi.id} variant="body2" color="text.secondary">
                        • {pi.investigation?.name}
                      </Typography>
                    ))}
                  </Box>
                )}
                {!lastPrescription.prescriptionMedicines?.length &&
                  !lastPrescription.prescriptionAdvices?.length &&
                  !lastPrescription.prescriptionInvestigations?.length && (
                  <Typography variant="body2" color="text.secondary">No details available.</Typography>
                )}
              </Paper>
            )}
          </Grid>

          {/* Center Panel - Prescription Editor */}
          <Grid item xs={12} md={6}>
            <Paper elevation={3} sx={{ p: 3, borderRadius: 2 }} className="print-area">

              {/* Screen Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }} className="no-print">
                <Typography variant="h6" sx={{ fontWeight: 600, pt: 0.5 }}>
                  Create Prescription
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<Print />}
                    onClick={handlePrint}
                    disabled={!canPrint}
                    sx={{ background: canPrint ? 'linear-gradient(135deg, #2196f3 0%, #1976d2 100%)' : undefined }}
                  >
                    Print Prescription
                  </Button>
                  {lastPrescription && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<ContentCopy />}
                      onClick={handleCopyLastPrescription}
                      sx={{ textTransform: 'none' }}
                    >
                      Copy Last Prescription
                    </Button>
                  )}
                </Box>
              </Box>

              {/* Print Header */}
              <Box className="print-only" sx={{ mb: 2, display: 'none' }}>
                {!isPrePrinted && (
                  <Box sx={{
                    background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 100%)',
                    color: '#fff',
                    textAlign: 'center',
                    py: 1.5,
                    px: 2,
                    mb: 1.5,
                    mx: -3,
                    mt: -3,
                  }}>
                    <Typography sx={{ fontWeight: 800, fontSize: '1.6rem', letterSpacing: 1, lineHeight: 1.2 }}>
                      {printSettings?.hospitalName || 'Vedanta Hospital'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.75rem', opacity: 0.9, mt: 0.3 }}>
                      {printSettings?.address || '123 Medical Street, City, State'}
                      &nbsp;|&nbsp;
                      {printSettings?.phone || '+91 123-456-7890'}
                    </Typography>
                  </Box>
                )}
                <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 1, mb: 1.5 }}>
                  <Grid container alignItems="flex-start">
                    <Grid item xs={4}>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        <strong>Patient:</strong> {patient?.firstName} {patient?.lastName}
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        <strong>Age:</strong> {patient ? calculateAge(patient.dateOfBirth) : ''} yrs
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        <strong>Gender:</strong> {patient?.gender}
                      </Typography>
                      {vitals.weight && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>Weight:</strong> {vitals.weight} kg
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'center' }}>
                      {patient?.bloodGroup && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>Blood Group:</strong> {patient.bloodGroup}
                        </Typography>
                      )}
                      {vitals.bp && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>BP:</strong> {vitals.bp} mmHg
                        </Typography>
                      )}
                      {vitals.pulse && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>Pulse:</strong> {vitals.pulse} bpm
                        </Typography>
                      )}
                      {vitals.spo2 && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>SpO{'₂'}:</strong> {vitals.spo2}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={4} sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        <strong>Rx No.:</strong> #{prescriptionId}
                      </Typography>
                      <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                        <strong>Date:</strong> {new Date().toLocaleDateString('en-IN')}
                      </Typography>
                      {vitals.gestationalAge && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>Gest. Age:</strong> {vitals.gestationalAge}
                        </Typography>
                      )}
                      {vitals.edd && (
                        <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                          <strong>EDD:</strong> {vitals.edd}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>

                {chiefComplaint && (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5 }}>
                    <Typography variant="body2">
                      <strong>Chief Complaint:</strong> {chiefComplaint}
                    </Typography>
                  </Box>
                )}

                {(provisionalDiagnosis || allergy) && (
                  <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, backgroundColor: '#f8fafc', px: 2, py: 0.75, mb: 1.5, display: 'flex', gap: 3 }}>
                    {provisionalDiagnosis && (
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>Provisional Diagnosis:</strong> {provisionalDiagnosis}
                      </Typography>
                    )}
                    {allergy && (
                      <Typography variant="body2" sx={{ flex: 1 }}>
                        <strong>Allergy:</strong> {allergy}
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>

              {/* Chief Complaint */}
              <Box className="no-print" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Chief Complaint
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={2}
                  size="small"
                  placeholder="Enter chief complaint..."
                  value={chiefComplaint}
                  onChange={(e) => setChiefComplaint(e.target.value)}
                />
              </Box>

              {/* Provisional Diagnosis + Allergy */}
              <Box className="no-print" sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Provisional Diagnosis
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Enter provisional diagnosis..."
                    value={provisionalDiagnosis}
                    onChange={(e) => setProvisionalDiagnosis(e.target.value)}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Allergy
                  </Typography>
                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    placeholder="Enter known allergies..."
                    value={allergy}
                    onChange={(e) => setAllergy(e.target.value)}
                  />
                </Box>
              </Box>

              {/* Prescribed Medicines — always visible on screen */}
              <Box className="no-print">
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Prescribed Medicines
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Medicine</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Dosage</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Duration</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Meal Preferences</TableCell>
                      <TableCell sx={{ fontWeight: 700 }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedMedicines.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} sx={{ textAlign: 'center', color: 'text.secondary', py: 2 }}>
                          No medicines added yet
                        </TableCell>
                      </TableRow>
                    ) : (
                      selectedMedicines.map((medicine) => (
                        <TableRow key={medicine.id}>
                          <TableCell sx={{ fontWeight: 500 }}>{medicine.name}</TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={medicine.dosage}
                              onChange={(e) => updateMedicine(medicine.id, 'dosage', formatDosage(e.target.value))}
                              placeholder="1-0-1"
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              size="small"
                              value={medicine.duration}
                              onChange={(e) => updateMedicine(medicine.id, 'duration', e.target.value)}
                              placeholder="7 days"
                              sx={{ width: 90 }}
                            />
                          </TableCell>
                          <TableCell>
                            <TextField
                              select
                              size="small"
                              value={medicine.mealPref}
                              onChange={(e) => updateMedicine(medicine.id, 'mealPref', e.target.value)}
                              sx={{ width: 130 }}
                            >
                              <MenuItemMUI value="">Any Time</MenuItemMUI>
                              <MenuItemMUI value="Before Meal">Before Meal</MenuItemMUI>
                              <MenuItemMUI value="After Meal">After Meal</MenuItemMUI>
                              <MenuItemMUI value="With Meal">With Meal</MenuItemMUI>
                              <MenuItemMUI value="Empty Stomach">Empty Stomach</MenuItemMUI>
                            </TextField>
                          </TableCell>
                          <TableCell>
                            <IconButton size="small" color="error" onClick={() => removeMedicine(medicine.id)}>
                              <Delete />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </Box>

              {/* Advice Given — always visible on screen */}
              <Box className="no-print" sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Advice Given
                </Typography>
                {selectedAdvices.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                    No advice added yet
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {selectedAdvices.map((advice) => (
                        <TableRow key={advice.id}>
                          <TableCell sx={{ fontSize: '0.875rem', py: 0.75 }}>
                            • {advice.text}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, width: 48 }}>
                            <IconButton size="small" color="error" onClick={() => removeAdvice(advice.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {/* Investigations Advised — always visible on screen */}
              <Box className="no-print" sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Investigations Advised
                </Typography>
                {selectedInvestigations.length === 0 ? (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                    No investigations added yet
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableBody>
                      {selectedInvestigations.map((investigation) => (
                        <TableRow key={investigation.id}>
                          <TableCell sx={{ fontSize: '0.875rem', py: 0.75 }}>
                            • {investigation.name}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 0.75, width: 48 }}>
                            <IconButton size="small" color="error" onClick={() => removeInvestigation(investigation.id)}>
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>

              {/* Medical Notes */}
              <Box className="no-print" sx={{ mt: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                  Medical Notes
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  size="small"
                  placeholder="Enter any important notes here..."
                  value={importantNotes}
                  onChange={(e) => setImportantNotes(e.target.value)}
                />
              </Box>

              {/* Follow-Up Date + Refer to Doctor */}
              <Box className="no-print" sx={{ mt: 2, display: 'flex', gap: 3, alignItems: 'flex-start' }}>
                <Box sx={{ flexShrink: 0 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Follow-Up Date
                  </Typography>
                  <TextField
                    type="date"
                    size="small"
                    value={followUpDate}
                    onChange={(e) => setFollowUpDate(e.target.value)}
                    inputProps={{ min: new Date().toISOString().split('T')[0] }}
                    sx={{ width: 200 }}
                  />
                </Box>

                <Box sx={{ flex: 1 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
                    Refer to Doctor
                  </Typography>
                  <Autocomplete
                    freeSolo
                    options={availableReferDoctors}
                    getOptionLabel={(option) =>
                      typeof option === 'string'
                        ? option
                        : option.specialization
                        ? `${option.name} (${option.specialization})`
                        : option.name
                    }
                    inputValue={referDoctor}
                    onInputChange={(_, value) => setReferDoctor(value)}
                    onChange={(_, value) => {
                      if (value && typeof value === 'object') {
                        setReferDoctor(value.name);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        size="small"
                        placeholder="Type or select a doctor..."
                        fullWidth
                      />
                    )}
                  />
                </Box>
              </Box>

              {/* Print-Only Section */}
              <Box className="print-only" sx={{ display: 'none' }}>
                {selectedMedicines.length > 0 && (
                  <Box sx={{ mb: 2 }}>
                    <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                        Medicines Prescribed
                      </Typography>
                    </Box>
                    <Table size="small" sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1, fontSize: '0.82rem' } }}>
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                          <TableCell sx={{ fontWeight: 700, width: '40%' }}>Medicine</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: '20%' }}>Dosage</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: '20%' }}>Duration</TableCell>
                          <TableCell sx={{ fontWeight: 700, width: '20%' }}>Meal</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedMedicines.map((medicine, idx) => (
                          <TableRow key={medicine.id} sx={{ backgroundColor: idx % 2 === 0 ? '#fff' : '#fafbfc' }}>
                            <TableCell>
                              <Typography sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{medicine.name}</Typography>
                              {medicine.composition && medicine.composition !== medicine.name && (
                                <Typography sx={{ fontSize: '0.72rem', color: '#666', fontStyle: 'italic' }}>
                                  {medicine.composition}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>{medicine.dosage}</TableCell>
                            <TableCell>
                              {medicine.duration
                                ? `${medicine.duration} ${medicine.duration === '1' ? 'day' : 'days'}`
                                : ''}
                            </TableCell>
                            <TableCell>{medicine.mealPref || ''}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </Box>
                )}

                {(selectedAdvices.length > 0 || selectedInvestigations.length > 0) && (
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    {selectedAdvices.length > 0 && (
                      <Grid item xs={selectedInvestigations.length > 0 ? 6 : 12}>
                        <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                            Advice Given
                          </Typography>
                        </Box>
                        {selectedAdvices.map((advice) => (
                          <Typography key={advice.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                            • {patient?.hindiPrescription && advice.hindiText ? advice.hindiText : advice.text}
                          </Typography>
                        ))}
                      </Grid>
                    )}
                    {selectedInvestigations.length > 0 && (
                      <Grid item xs={selectedAdvices.length > 0 ? 6 : 12}>
                        <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                            Investigations Advised
                          </Typography>
                        </Box>
                        {selectedInvestigations.map((investigation) => (
                          <Typography key={investigation.id} sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                            • {investigation.name}
                          </Typography>
                        ))}
                      </Grid>
                    )}
                  </Grid>
                )}

                {importantNotes && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                        Medical Notes
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                      {importantNotes}
                    </Typography>
                  </Box>
                )}

                {(followUpDate || referDoctor) && (
                  <Box sx={{ mt: 1.5, display: 'flex', gap: 4 }}>
                    {followUpDate && (
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                            Follow-Up Date
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                          {new Date(followUpDate).toLocaleDateString('en-IN', { day: '2-digit', month: 'long', year: 'numeric' })}
                        </Typography>
                      </Box>
                    )}
                    {referDoctor && (
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ borderLeft: '3px solid #1565c0', pl: 1, mb: 0.75 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: '0.8rem', textTransform: 'uppercase', letterSpacing: 1, color: '#1565c0' }}>
                            Refer to Doctor
                          </Typography>
                        </Box>
                        <Typography sx={{ fontSize: '0.82rem', lineHeight: 1.8, pl: 0.5 }}>
                          {referDoctor}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                )}
              </Box>

              {/* Print Footer */}
              <Box className="print-only" sx={{ mt: 3, display: 'none' }}>
                <Divider sx={{ mb: 1.5, borderColor: '#1565c0' }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                  <Typography sx={{ fontSize: '0.72rem', color: '#888' }}>
                    This prescription is computer generated.
                  </Typography>
                  <Box sx={{ textAlign: 'right' }}>
                    <Typography sx={{ fontWeight: 700, fontSize: '0.85rem' }}>
                      Dr. {user?.firstName} {user?.lastName}
                    </Typography>
                    {user?.education && (
                      <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                        {user.education}
                      </Typography>
                    )}
                    {user?.medicalRegistrationNumber && (
                      <Typography sx={{ fontSize: '0.78rem', color: '#555' }}>
                        Reg. No.: {user.medicalRegistrationNumber}
                      </Typography>
                    )}
                    <Typography sx={{ fontSize: '0.82rem', mt: 6, borderTop: '1px solid #555', pt: 0.5, minWidth: 160, textAlign: 'center' }}>
                      Signature
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* Right Panel - Available Items (Medicines / Advice / Investigations) + Lab Reports */}
          <Grid item xs={12} md={3} className="no-print">
            <Box sx={{ position: 'sticky', top: 16, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Tabs
                value={tabValue}
                onChange={(_, newValue) => setTabValue(newValue)}
                variant="fullWidth"
                sx={{ borderBottom: '1px solid #e0e0e0', minHeight: 42 }}
              >
                <Tab label="Medicines" sx={{ fontSize: '0.75rem', minHeight: 42, py: 0.5 }} />
                <Tab label="Advice" sx={{ fontSize: '0.75rem', minHeight: 42, py: 0.5 }} />
                <Tab label="Investigations" sx={{ fontSize: '0.75rem', minHeight: 42, py: 0.5 }} />
              </Tabs>

              {/* ~10 rows: 34px/row × 10 + search 45px = 385px */}
              <Box sx={{ maxHeight: 385, overflowY: 'auto' }}>
                {tabValue === 0 && (
                  <>
                    <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <TextField
                        fullWidth size="small"
                        placeholder="Search medicines..."
                        value={medicineSearch}
                        onChange={(e) => setMedicineSearch(e.target.value)}
                        autoComplete="off"
                        inputProps={{ style: { fontSize: '0.82rem' } }}
                      />
                    </Box>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8' }}>Medicine</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8', width: 64 }}>Hotkey</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8', width: 36 }}>â­</TableCell>
                        </TableRow>
                      </TableHead>
                                            <TableBody>
                        {medicinesTabRows}
                      </TableBody>
                    </Table>
                  </>
                )}

                {tabValue === 1 && (
                  <>
                    <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <TextField
                        fullWidth size="small"
                        placeholder="Search advice..."
                        value={adviceSearch}
                        onChange={(e) => setAdviceSearch(e.target.value)}
                        autoComplete="off"
                        inputProps={{ style: { fontSize: '0.82rem' } }}
                      />
                    </Box>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8' }}>Advice</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8', width: 64 }}>Hotkey</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8', width: 36 }}>â­</TableCell>
                        </TableRow>
                      </TableHead>
                                            <TableBody>
                        {adviceTabRows}
                      </TableBody>
                    </Table>
                  </>
                )}

                {tabValue === 2 && (
                  <>
                    <Box sx={{ p: 1, borderBottom: '1px solid #e0e0e0' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Search investigations..."
                        value={investigationSearch}
                        onChange={(e) => setInvestigationSearch(e.target.value)}
                        autoComplete="off"
                        inputProps={{ style: { fontSize: '0.82rem' } }}
                      />
                    </Box>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8' }}>Investigation</TableCell>
                          <TableCell sx={{ fontWeight: 700, fontSize: '0.78rem', backgroundColor: '#f0f4f8', width: 70 }}>Hotkey</TableCell>
                        </TableRow>
                      </TableHead>
                                            <TableBody>
                        {investigationsTabRows}
                      </TableBody>
                    </Table>
                  </>
                )}
              </Box>
            </Paper>

            {/* Lab Reports Panel */}
            <Paper elevation={3} sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <Box sx={{ px: 1.5, py: 1, bgcolor: '#f0f4f8', borderBottom: '1px solid #e0e0e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                <Science sx={{ fontSize: 16, color: '#1565c0' }} />
                <Typography sx={{ fontWeight: 700, fontSize: '0.78rem', color: '#1565c0', letterSpacing: 0.3 }}>
                  LAB REPORTS
                </Typography>
                <Typography sx={{ ml: 'auto', fontSize: '0.72rem', color: 'text.secondary' }}>
                  {labReports.length} file{labReports.length !== 1 ? 's' : ''}
                </Typography>
              </Box>
              <Box sx={{ maxHeight: 220, overflowY: 'auto' }}>
                {labReports.length === 0 ? (
                  <Typography sx={{ fontSize: '0.78rem', color: 'text.secondary', textAlign: 'center', py: 2 }}>
                    No reports uploaded yet
                  </Typography>
                ) : (
                  labReports.map((lr) => (
                    <Box
                      key={lr.id}
                      onClick={() => { setViewingReport(lr); setReportViewerOpen(true); }}
                      sx={{
                        display: 'flex', alignItems: 'center', gap: 1,
                        px: 1.5, py: 0.9,
                        borderBottom: '1px solid #f0f0f0',
                        cursor: 'pointer',
                        '&:hover': { bgcolor: '#e3f2fd' },
                      }}
                    >
                      <Science sx={{ fontSize: 14, color: '#1565c0', flexShrink: 0 }} />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {lr.reportType || 'Report'}
                        </Typography>
                        <Typography sx={{ fontSize: '0.68rem', color: 'text.secondary', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {lr.fileName}
                        </Typography>
                        <Typography sx={{ fontSize: '0.65rem', color: 'text.disabled' }}>
                          {new Date(lr.uploadedAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          {lr.labName ? ` · ${lr.labName}` : ''}
                        </Typography>
                      </Box>
                      <OpenInNew sx={{ fontSize: 13, color: '#90a4ae', flexShrink: 0 }} />
                    </Box>
                  ))
                )}
              </Box>
            </Paper>

            </Box>{/* end sticky wrapper */}
          </Grid>
        </Grid>
      </Container>

      {/* Lab Report Viewer Dialog */}
      <Dialog open={reportViewerOpen} onClose={() => setReportViewerOpen(false)} maxWidth="lg" fullWidth PaperProps={{ sx: { height: '90vh' } }}>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Science sx={{ color: '#1565c0' }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>{viewingReport?.reportType || 'Lab Report'}</Typography>
              <Typography variant="caption" color="text.secondary">{viewingReport?.fileName}</Typography>
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" startIcon={<OpenInNew />} onClick={() => window.open(`${API_BASE}/${(viewingReport?.fileUrl || '').replace(/\\/g, '/')}`, '_blank')}>
              Open in new tab
            </Button>
            <Button size="small" onClick={() => setReportViewerOpen(false)}>Close</Button>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0, display: 'flex', flexDirection: 'column' }}>
          {viewingReport && (() => {
            const url = `${API_BASE}/${viewingReport.fileUrl.replace(/\\/g, '/')}`;
            const isImage = /\.(jpg|jpeg|png|bmp|webp|tiff?)$/i.test(viewingReport.fileName);
            return isImage ? (
              <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'auto', p: 2 }}>
                <img src={url} alt={viewingReport.fileName} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
              </Box>
            ) : (
              <iframe
                src={url}
                title={viewingReport.fileName}
                style={{ flex: 1, width: '100%', height: '100%', border: 'none' }}
              />
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Vitals History Dialog */}
      <Dialog open={vitalsHistoryOpen} onClose={() => setVitalsHistoryOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Vitals History
          <IconButton
            onClick={() => {
              const chartEl = document.getElementById('vitals-chart-area');
              if (!chartEl) return;
              const popup = window.open('', '_blank', 'width=1000,height=700');
              popup.document.write(`<!DOCTYPE html>
<html>
<head>
  <title>Vitals History - ${patient?.firstName || ''} ${patient?.lastName || ''}</title>
  <style>
    body { font-family: Arial, sans-serif; margin: 20px; }
    h2 { text-align: center; margin-bottom: 20px; font-size: 1.1rem; color: #333; }
    * { box-sizing: border-box; }
  </style>
</head>
<body>
  <h2>Vitals History — ${patient?.firstName || ''} ${patient?.lastName || ''} | Age: ${patient ? calculateAge(patient.dateOfBirth) : ''} yrs | ${patient?.gender || ''}</h2>
  ${chartEl.outerHTML}
  <script>window.onload = function() { window.print(); window.onafterprint = function() { window.close(); }; };<\/script>
</body>
</html>`);
              popup.document.close();
            }}
            size="small"
            title="Print Charts"
          >
            <Print />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {vitalsHistoryData.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No Vitals Data Found
            </Typography>
          ) : (
            <Grid container spacing={3} id="vitals-chart-area">
              {/* BP Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Blood Pressure (mmHg)
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={vitalsHistoryData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v, name) => [`${v} mmHg`, name === 'bp' ? 'Systolic' : 'Diastolic']} />
                    <Legend formatter={(value) => value === 'bp' ? 'Systolic' : 'Diastolic'} />
                    <Line type="monotone" dataKey="bp" stroke="#e53935" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                    <Line type="monotone" dataKey="bpDiastolic" stroke="#1565c0" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              {/* Pulse Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Pulse (bpm)
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={vitalsHistoryData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v} bpm`, 'Pulse']} />
                    <Line type="monotone" dataKey="pulse" stroke="#1e88e5" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              {/* Weight Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  Weight (kg)
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={vitalsHistoryData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip formatter={(v) => [`${v} kg`, 'Weight']} />
                    <Line type="monotone" dataKey="weight" stroke="#43a047" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>

              {/* SPO2 Chart */}
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                  SpO{'₂'} (%)
                </Typography>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={vitalsHistoryData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} domain={[80, 100]} />
                    <Tooltip formatter={(v) => [`${v}%`, 'SpO₂']} />
                    <Line type="monotone" dataKey="spo2" stroke="#8e24aa" strokeWidth={2} dot={{ r: 4 }} connectNulls />
                  </LineChart>
                </ResponsiveContainer>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setVitalsHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Prescription History Dialog */}
      <Dialog open={historyOpen} onClose={() => setHistoryOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Prescription History</DialogTitle>
        <DialogContent dividers>
          {patientHistory.length === 0 ? (
            <Typography color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
              No Prescription History Found
            </Typography>
          ) : (
            [...patientHistory]
              .sort((a, b) => new Date(b.date) - new Date(a.date))
              .map((rx) => (
                <Box key={rx.id} sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                      Rx #{rx.id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(rx.date).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    </Typography>
                  </Box>
                  {rx.prescriptionMedicines?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Medicines:</Typography>
                      <Table size="small">
                        <TableHead>
                          <TableRow sx={{ backgroundColor: '#f0f4f8' }}>
                            <TableCell sx={{ fontWeight: 600 }}>Medicine</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Dosage</TableCell>
                            <TableCell sx={{ fontWeight: 600 }}>Duration</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rx.prescriptionMedicines.map((m) => (
                            <TableRow key={m.id}>
                              <TableCell>{m.medicine?.name}</TableCell>
                              <TableCell>{m.dosage}</TableCell>
                              <TableCell>{m.duration}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </Box>
                  )}
                  {rx.prescriptionAdvices?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Advice:</Typography>
                      {rx.prescriptionAdvices.map((a) => (
                        <Typography key={a.id} variant="body2" color="text.secondary">• {a.advice?.text}</Typography>
                      ))}
                    </Box>
                  )}
                  {rx.prescriptionInvestigations?.length > 0 && (
                    <Box sx={{ mb: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>Investigations:</Typography>
                      {rx.prescriptionInvestigations.map((pi) => (
                        <Typography key={pi.id} variant="body2" color="text.secondary">• {pi.investigation?.name}</Typography>
                      ))}
                    </Box>
                  )}
                  <Divider sx={{ mt: 2 }} />
                </Box>
              ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Print Confirmation Dialog */}
      <Dialog open={printConfirmOpen} disableEscapeKeyDown>
        <DialogTitle sx={{ fontWeight: 700, textAlign: 'center', pt: 3 }}>
          Did the prescription print successfully?
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 1 }}>
          <Typography variant="body2" color="text.secondary">
            If the prescription did not reach the printer, you can print it again.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', gap: 2, pb: 3, px: 3 }}>
          <Button
            variant="outlined"
            startIcon={<Print />}
            onClick={handlePrintAgain}
            sx={{ minWidth: 150 }}
          >
            Print Again
          </Button>
          <Button
            variant="contained"
            startIcon={<CheckCircle />}
            onClick={handlePrintConfirmYes}
            color="success"
            sx={{ minWidth: 150 }}
          >
            Yes, Done
          </Button>
        </DialogActions>
      </Dialog>

      <style>{`
        @media print {
          @page { margin: ${mt}mm ${mr}mm ${mb}mm ${ml}mm; }
          .no-print {
            display: none !important;
          }
          .print-only {
            display: block !important;
          }
          .print-area {
            box-shadow: none !important;
            border: none !important;
          }
        }

      `}</style>
    </div>
  );
};

export default PatientDetailPage;
